
import Vue, { PropType } from 'vue';
import i18n from '~/localization/i18n';

// Services and types
import { SchoolVerification, SchoolVerificationState } from '~/services/SchoolService';
import SchoolVerificationService from '~/services/SchoolVerificationService';
import { CandidateStudyProgram } from '~/services/CSPService';

// Assets
import verifiedBadge from '~/assets/img/verifiedBadge.png';
import pendingBadge from '~/assets/img/pendingBadge.png';
import postponedBadge from '~/assets/img/postponedBadge.png';

interface StudyProgramDetail {
    schoolId: number | null;
    schoolName: string;
}

export default Vue.extend({
    name: 'StudyProgramStatusIcon',
    props: {
        program: {
            type: Object as PropType<CandidateStudyProgram>,
            required: true,
        },
    },
    data() {
        return {
            verificationService: new SchoolVerificationService(),

            verifiedBadge,
            pendingBadge,
            postponedBadge,
        };
    },
    computed: {
        details(): StudyProgramDetail {
            const p = this.program;

            const schoolId = p.studyProgram.school ? p.studyProgram.school.id : null;
            const schoolName = p.studyProgram.school
                ? p.studyProgram.school.name
                : p.studyProgram.customSchoolName || '';

            return {
                schoolId,
                schoolName,
            };
        },
        verification(): SchoolVerification | undefined {
            return this.program.verification;
        },
        isVerified(): boolean {
            return (
                !!this.verification && this.verification.state === SchoolVerificationState.verified
            );
        },
        isIgnored(): boolean {
            return (
                !!this.verification && this.verification.state === SchoolVerificationState.ignored
            );
        },
        isUnverified(): boolean {
            return (
                !this.verification ||
                this.verification.state === SchoolVerificationState.needVerification
            );
        },
        isPending(): boolean {
            return (
                !!this.verification && this.verification.state === SchoolVerificationState.pending
            );
        },
        verificationStatusText(): string {
            const key = this.isVerified
                ? 'verified'
                : this.isIgnored
                ? 'ignored'
                : this.isUnverified
                ? 'unverified'
                : this.isPending
                ? 'pending'
                : '';

            if (!key) {
                return '';
            }

            return this.$t(`shared.schoolVerification.statusTooltips.${key}`, {
                schoolName: this.details.schoolName,
            }).toString();
        },
        verificationStatusIcon(): string {
            if (this.isVerified) {
                return verifiedBadge;
            }

            if (this.isIgnored) {
                return postponedBadge;
            }

            if (this.isUnverified) {
                return pendingBadge;
            }

            if (this.isPending) {
                return pendingBadge;
            }

            return '';
        },
    },
    methods: {
        handleVerificationIconClick(): void {
            if (!this.verification) {
                return;
            }

            switch (this.verification.state) {
                case SchoolVerificationState.ignored:
                    this.unignoreVerification();
                    break;
            }
        },
        unignoreVerification(): void {
            if (!this.details.schoolId) {
                return;
            }

            this.verificationService
                .updateState(this.details.schoolId, SchoolVerificationState.needVerification)
                .then(() => {
                    this.reloadVerifications();
                    this.$interfaces.messageModule.addNewMessage({
                        title: i18n.t(
                            'components.miscellaneous.verifyModule.verificationUnignoredTitle'
                        ),
                        message: i18n.t(
                            'components.miscellaneous.verifyModule.verificationUnignoredMessage'
                        ),
                    });
                })
                .catch(() => {
                    this.$interfaces.messageModule.addNewMessage({
                        state: 'ERROR',
                        title: i18n.t(
                            'components.miscellaneous.verifyModule.statusChangeErrorTitle'
                        ),
                        message: i18n.t(
                            'components.miscellaneous.verifyModule.statusChangeErrorMessage'
                        ),
                    });
                });
        },
        reloadVerifications() {
            this.$interfaces.university.connection.get({ forceUpdate: true });
        },
    },
});
